import { getBaseUrlFrontend } from '@/lib/helper/environment/baseUrl';
import { useCallback, useState } from 'react';
type UseKlaviyoApiOptions = {
  companyId: string;
};

export const useKlaviyoApi = ({ companyId }: UseKlaviyoApiOptions) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Get a fresh access token, refreshing if necessary
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const response = await fetch(getBaseUrlFrontend() + '/api/klaviyo/token/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ companyId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get access token');
      }

      const data = await response.json();
      return data.access_token;
    } catch (err) {
      console.error('Error getting access token:', err);
      throw err;
    }
  }, [companyId]);

  /**
   * Make an authenticated request to the Klaviyo API
   */
  const fetchFromKlaviyo = useCallback(async <T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> => {
    setLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessToken();
      
      if (!accessToken) {
        throw new Error('No access token available');
      }

      // Merge provided options with authorization header
      const requestOptions: RequestInit = {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      };

      // Determine the base URL based on the endpoint
      const baseUrl = endpoint.startsWith('/api/v1') 
        ? 'https://a.klaviyo.com' 
        : 'https://a.klaviyo.com/api';

      const response = await fetch(`${baseUrl}${endpoint}`, requestOptions);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || errorData.message || `Error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json() as T;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  /**
   * Disconnect the Klaviyo integration for a company
   */
  const disconnectKlaviyo = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      // First, try to revoke the token (best practice)
      try {
        const accessToken = await getAccessToken();
        if (accessToken) {
          // Get the Basic auth to revoke the token
          const response = await fetch(getBaseUrlFrontend() + '/api/klaviyo/token/revoke', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              companyId, 
              accessToken 
            }),
          });

          if (!response.ok) {
            console.warn('Failed to revoke token, but will continue with disconnection');
          }
        }
      } catch (err) {
        // Continue even if token revocation fails
        console.warn('Error revoking token:', err);
      }

      // Delete the connection from the database
      const response = await fetch('/api/klaviyo/connection', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ companyId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to disconnect from Klaviyo');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId, getAccessToken]);

  return {
    fetchFromKlaviyo,
    disconnectKlaviyo,
    loading,
    error,
  };
};

export default useKlaviyoApi; 